import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

interface TrialStatus {
  is_trial: boolean;
  has_subscription: boolean;
  expired: boolean;
  reason?: 'time' | 'minutes' | 'no_subscription' | null;
  days_left?: number;
  minutes_left?: number;
  minutes_used?: number;
  minutes_allocated?: number;
  trial_started_at?: string;
  trial_expires_at?: string;
  plan_code?: string;
}

export function useTrialStatus() {
  const { user } = useAuth();
  const [trialStatus, setTrialStatus] = useState<TrialStatus | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchTrialStatus = useCallback(async () => {
    if (!user?.id) {
      setTrialStatus(null);
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      const { data, error: rpcError } = await supabase.rpc('check_trial_status', {
        _user_id: user.id,
      });

      if (rpcError) {
        console.error('Erro ao verificar status do trial:', rpcError);
        setError(rpcError.message);
        return;
      }

      setTrialStatus(data as unknown as TrialStatus);
      setError(null);
    } catch (err) {
      console.error('Erro ao verificar status do trial:', err);
      setError('Erro ao verificar status do trial');
    } finally {
      setIsLoading(false);
    }
  }, [user?.id]);

  useEffect(() => {
    fetchTrialStatus();
  }, [fetchTrialStatus]);

  // Helpers
  const isTrial = trialStatus?.is_trial ?? false;
  const isExpired = trialStatus?.expired ?? false;
  const hasSubscription = trialStatus?.has_subscription ?? false;
  const isError = !!error;
  
  const daysLeft = trialStatus?.days_left ?? 0;
  const minutesLeft = trialStatus?.minutes_left ?? 0;
  const minutesUsed = trialStatus?.minutes_used ?? 0;
  const minutesAllocated = trialStatus?.minutes_allocated ?? 0;
  
  const usagePercentage = minutesAllocated > 0 
    ? Math.min(100, (minutesUsed / minutesAllocated) * 100) 
    : 0;

  const canRecord = hasSubscription && !isExpired;
  
  const expirationReason = trialStatus?.reason;

  return {
    trialStatus,
    isLoading,
    error,
    refetch: fetchTrialStatus,
    
    // Helpers
    isTrial,
    isExpired,
    isError,
    hasSubscription,
    daysLeft,
    minutesLeft,
    minutesUsed,
    minutesAllocated,
    usagePercentage,
    canRecord,
    expirationReason,
  };
}
